/**
 * Drop on the product page (bot/SPEC.md §6, board V1-Early).
 *
 * States by the clock (pages are cached, Liquid only guesses — snippet kh-drop-info):
 * - soon: before the start (with early access — before the early start): «Нагадати» / «Отримати на добу раніше»;
 * - early: early access, a day before the public start: «Купити зараз через бота»;
 *   with the bot's personal link in this browser — «Доступ відкрито» and the usual button;
 * - none: on sale for everyone — the usual page, «Новинка».
 * The state goes to [data-kh-drop-state] / [data-kh-drop-access] on the product component, CSS hides the
 * usual status and button until the start or access.
 *
 * Personal link from the bot: /products/<handle>?ea=<token>. The token is kept in localStorage per product and
 * goes into the cart attribute `kh_early` («<product>:<token>,…»): Walnut carries cart attributes into the
 * Shopify order, where the bot service checks it (the theme only hides the button; the order check is the rule).
 */

const TOKEN_KEY = 'kh-early';
const TOKEN = /^[A-Za-z0-9-]{16,40}$/;

/** @returns {Record<string, string>} */
function readTokens() {
  try {
    const value = JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}');
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}

/** @param {Record<string, string>} tokens */
function writeTokens(tokens) {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  } catch {
    /* private mode: access lasts for this page view */
  }
}

/** @param {number} ms */
function formatLeft(ms) {
  const minutes = Math.max(0, Math.ceil(ms / 60000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

class KhDrop extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #access = false;
  /** @type {string | null} */
  #token = null;

  get #product() {
    return this.dataset.product || '';
  }

  get #root() {
    return /** @type {HTMLElement | null} */ (this.closest('.kh-pdp'));
  }

  connectedCallback() {
    this.#token = this.#captureToken();
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
    if (this.#token) this.#checkAccess(this.#token);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  /** @returns {'soon' | 'early' | 'none'} */
  #state(now = Date.now()) {
    const publicAt = Number(this.dataset.public) * 1000;
    const earlyAt = Number(this.dataset.early) * 1000;
    if (!publicAt) return 'soon'; // old «soon» tag without a date
    if (now >= publicAt) return 'none';
    if (earlyAt && now >= earlyAt) return 'early';
    return 'soon';
  }

  /** ?ea=<token> from the bot: keep it for this product and drop it from the address bar. */
  #captureToken() {
    const tokens = readTokens();
    const url = new URL(window.location.href);
    const fresh = url.searchParams.get('ea');
    if (fresh !== null) {
      url.searchParams.delete('ea');
      history.replaceState(history.state, '', url);
      if (TOKEN.test(fresh)) {
        tokens[this.#product] = fresh;
        writeTokens(tokens);
      }
    }
    return tokens[this.#product] || null;
  }

  /** @param {string} token */
  async #checkAccess(token) {
    const api = (this.dataset.api || '').replace(/\/$/, '');
    let ok = true; // without the service (or offline) trust the link: the order check decides
    if (api) {
      try {
        const query = `t=${encodeURIComponent(token)}&p=${encodeURIComponent(this.#product)}`;
        const response = await fetch(`${api}/api/early?${query}`, { cache: 'no-store' });
        if (response.ok) ok = Boolean((await response.json()).ok);
      } catch {
        /* service down: keep ok */
      }
    }
    if (!ok) {
      const tokens = readTokens();
      delete tokens[this.#product];
      writeTokens(tokens);
      this.#token = null;
      return;
    }
    this.#access = true;
    this.#tick();
    await this.#saveToCart();
  }

  /** Cart attribute kh_early = every token this browser holds, «<product>:<token>,…». */
  async #saveToCart() {
    const value = Object.entries(readTokens())
      .map(([product, token]) => `${product}:${token}`)
      .join(',');
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { kh_early: value } }),
      });
    } catch {
      /* the next page view tries again */
    }
  }

  #tick() {
    const now = Date.now();
    const state = this.#state(now);
    const access = state === 'early' && this.#access;
    const root = this.#root;
    if (root) {
      if (state === 'none') delete root.dataset.khDropState;
      else root.dataset.khDropState = state;
      root.toggleAttribute('data-kh-drop-access', access);
    }

    this.querySelectorAll('[data-kh-drop-panel]').forEach((panel) => {
      const name = panel.getAttribute('data-kh-drop-panel');
      const show = (name === 'soon' && state === 'soon') || (name === 'early' && state === 'early' && !access) || (name === 'access' && access);
      /** @type {HTMLElement} */ (panel).hidden = !show;
    });

    const scope = root || document;
    scope.querySelectorAll('[data-kh-drop-badge]').forEach((badge) => {
      /** @type {HTMLElement} */ (badge).hidden = badge.getAttribute('data-kh-drop-badge') !== state;
    });
    const left = formatLeft(Number(this.dataset.public) * 1000 - now);
    scope.querySelectorAll('[data-kh-drop-left]').forEach((element) => {
      /** @type {HTMLElement} */ (element).hidden = state !== 'early';
      const value = element.querySelector('[data-kh-drop-left-value]');
      if (value && value.textContent !== left) value.textContent = left;
    });

    if (state === 'none') window.clearInterval(this.#timer);
  }
}

if (!customElements.get('kh-drop')) customElements.define('kh-drop', KhDrop);
